// The shipping lane as a map object (v2-the-deep.md §7.2, §18.1; mockup
// round 088 card 09; R2646, R2658).
//
// A lane of the faction is a pick of chart card 16: a press on its track
// opens the lane card in the context slot, as a press on a battle mark
// opens its film (`Commands.pressMark`, the `onMark` of `fast/main.ts`).
// The mark id names the commander and the plot, `lane:<owner>:<plot>`,
// where owner 0 is an own plot (`CoursePlot.owner` is 0 on an own plot).
// A long press of a finger peeks the same card.
//
// The card names the lane, its commander, its legs and its length, the
// own fleets that a lane light lifts on it (the condition `lane` of the
// wire, under `sapper`), and SEND, which sends the own fleet the reader
// selected last on the legs of the lane, one fleet for each press
// (R1307). An own lane also carries RENAME and the LANE switch that clears
// it.
//
// The network of the lanes (v2-the-deep.md §7.2 rules 11 to 14, R3074): the
// card names the road of the lane with its length and its commanders, the
// commanders whose lane lights stand on it, the lane credit in fleet hours,
// the quiet lane in the chip and the note, and each lane gap with VIEW to
// the chain point that closes it. The wire carries every figure; the card
// adds the lengths of the lanes of one road.
//
// Everything here is pure.

import { ConditionKey, type CoursePlot } from '../../gen/proto/aquaphobia';
import { card as S, overlay as OS } from '../../gen/strings';
import { mpxToPx } from '../../world';
import { type GameState, num } from '../../state';
import { HEX_SIZE } from '../../world';
import { esc } from '../esc';
import { cssColor } from '../format';
import { laneRoad, plotLegs, plotSpanPx } from '../overlay/routes';
import { loopParts } from '../plotline';
import type { Action, CardSize, CardSpec, Fact } from './card';
import { markSvg } from './marks';
import type { LaneRef } from './lanemark';

export { laneMarkId, laneOfMark, type LaneRef } from './lanemark';


/** The plot of a lane, or undefined when it is no lane any more. */
export function findLane(s: Pick<GameState, 'plots' | 'factionLanes'>, ref: LaneRef): CoursePlot | undefined {
  return ref.owner === 0
    ? s.plots.find((p) => p.id === ref.id && p.lane)
    : s.factionLanes.find((p) => p.id === ref.id && p.owner === ref.owner);
}

/** The distance of a point to a segment, world px. */
function segDist(x: number, y: number, x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0, dy = y1 - y0;
  const len2 = dx * dx + dy * dy;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - x0) * dx + (y - y0) * dy) / len2)) : 0;
  return Math.hypot(x - (x0 + t * dx), y - (y0 + t * dy));
}

/** The legs of a plot as segments, with the leg back of each loop part,
 *  as chart card 16 draws them (R2729). */
function laneSegments(p: CoursePlot): [number, number, number, number][] {
  const legs = plotLegs(p);
  const out: [number, number, number, number][] = [];
  for (let i = 1; i < legs.length; i++) out.push([legs[i - 1].x, legs[i - 1].y, legs[i].x, legs[i].y]);
  for (const part of loopParts(legs)) {
    out.push([legs[part.last].x, legs[part.last].y, legs[part.open].x, legs[part.open].y]);
  }
  return out;
}

/** Does a point stand on the lane: inside half a hex of a leg, the reach
 *  at which the hub reads the lane cells (§7.2 point 4)? */
export function onLane(p: CoursePlot, x: number, y: number): boolean {
  return laneSegments(p).some(([x0, y0, x1, y1]) => segDist(x, y, x0, y0, x1, y1) <= HEX_SIZE);
}

/** An own fleet as the lane card reads it. */
export interface LaneFleet {
  id: number;
  name: string;
  x: number;
  y: number;
  conditions?: readonly { key: ConditionKey; pct: number }[];
}

/** The own fleets on the lane that a lane light lifts now, and the factor
 *  of the lift the wire states (the condition `lane`, R2651). */
export function laneLit(p: CoursePlot, fleets: readonly LaneFleet[]): { n: number; pct: number } {
  let n = 0, pct = 0;
  for (const f of fleets) {
    const c = f.conditions?.find((k) => k.key === ConditionKey.CONDITION_KEY_LANE);
    if (!c || !onLane(p, f.x, f.y)) continue;
    n++;
    pct = Math.max(pct, c.pct);
  }
  return { n, pct };
}

/** The lane gap `i` of a lane as a point in world px, or null. */
export function laneGapAt(p: CoursePlot, i: number): { x: number; y: number } | null {
  const g = p.gaps[i];
  return g ? { x: mpxToPx(g.xMpx), y: mpxToPx(g.yMpx) } : null;
}

/** The facts of the network of a lane (rules 11 to 14): the road, the
 *  lighters, the lane credit and the gaps. */
function networkFacts(s: Pick<GameState, 'plots' | 'factionLanes'>, p: CoursePlot): Fact[] {
  const out: Fact[] = [];
  const road = laneRoad(s, p);
  if (road) {
    out.push({
      key: S.f_road,
      val: esc(S.lane_road_val({ road: road.name, px: num(Math.round(road.px)), who: S.lane_commanders({ n: road.commanders }) })),
    });
  }
  out.push({
    key: S.f_lighters,
    val: esc(p.lighters.length > 0
      ? p.lighters.map((l) => S.lane_lighter({ name: l.name, n: l.lights })).join(' · ')
      : S.lane_lighters_none),
  });
  out.push({
    key: S.f_lane_credit, title: S.lane_credit_title,
    val: esc(p.liftMin > 0 ? S.lane_credit({ h: (p.liftMin / 60).toFixed(1) }) : S.lane_credit_none),
  });
  if (p.gaps.length > 0) {
    out.push({ key: S.f_lane_gap, val: esc(p.gaps.map((g) => S.lane_gap({ leg: g.leg })).join(' · ')) });
  }
  return out;
}

/** What the lane card reads beside the plot. */
export interface LaneCardCtx {
  s: Pick<GameState, 'faction' | 'hasFeatureFlag' | 'plots' | 'factionLanes'>;
  /** The own fleets at sea. */
  fleets: readonly LaneFleet[];
  /** The own fleet SEND sends: the one the reader selected last, or null. */
  fleet: { id: number; name: string } | null;
}

/** The card of one lane of the faction (round 088 card 09). */
export function laneCard(ctx: LaneCardCtx, ref: LaneRef, p: CoursePlot, size: CardSize = 'card'): CardSpec {
  const own = ref.owner === 0;
  const legs = plotLegs(p);
  const loops = loopParts(legs).length;
  const route = [
    OS.routes_legs({ n: legs.length }),
    ...(loops > 0 ? [OS.routes_loops({ n: loops })] : []),
    OS.routes_px({ px: num(Math.round(plotSpanPx(legs))) }),
  ].join(' · ');
  const facts: Fact[] = [
    { key: S.f_commander, val: esc(own ? S.lane_you : p.ownerName) },
    { key: S.f_route, val: esc(route) },
  ];
  // The lane light lifts own fleets on an own lane (§11.3); the wire states
  // the lift as the condition `lane` while `sapper` is latched.
  if (ctx.s.hasFeatureFlag('sapper')) {
    const lit = laneLit(p, ctx.fleets);
    facts.push({
      key: S.f_lane_light, title: S.lane_lit_title,
      val: esc(lit.n > 0 ? S.lane_lit({ n: lit.n, f: (lit.pct / 100).toFixed(2) }) : S.lane_unlit),
    });
  }
  facts.push(...networkFacts(ctx.s, p));
  const f = ctx.fleet;
  const actions: Action[] = [{
    act: 'lanego', id: p.id, n: ref.owner, face: 'dark', off: f === null,
    label: f ? S.lane_send({ name: f.name }) : S.lane_send_none,
    title: f ? S.lane_send_title({ name: f.name }) : S.lane_send_none_title,
  }];
  // VIEW of each lane gap (rule 14): the map goes to the chain point.
  p.gaps.forEach((g, i) => actions.push({
    act: 'lanegap', id: p.id, n: ref.owner, dataKey: String(i), face: 'quiet',
    label: S.lane_gap_view({ leg: g.leg }), title: S.lane_gap_title,
  }));
  if (own) {
    actions.push(
      { act: 'plotname', id: p.id, face: 'quiet', label: OS.routes_rename },
      { act: 'plotlane', id: p.id, face: 'quiet', label: OS.routes_lane, on: true, title: OS.routes_lane_on_title },
    );
  }
  return {
    size, mark: markSvg('lane', cssColor(ctx.s.faction)), title: p.name, ink: { faction: ctx.s.faction },
    kind: S.lane_kind, status: { text: p.quiet ? S.lane_chip_quiet : own ? S.lane_chip_own : S.lane_chip },
    close: size !== 'peek', facts,
    note: { lines: p.quiet ? [esc(S.lane_quiet_note), esc(S.lane_note)] : [esc(S.lane_note)] }, actions,
  };
}
